import { checkInValidAnswer, redirectTo404Page } from "../shared/AppFormHelper";
import { ControlType } from "../enums/ControlType";
import { SetupQuestion } from "../enums/SetupQuestionData";
import {
    IAnswerProps,
    IAppFormQuestion,
    IApplicantInfo
} from "../interfaces/IAppFormData";
import { IAppFormSelectors } from "./useAppFormState";
import { getDataLayerInfo } from "../api/ApiAppForm";

interface IUseAFForm {
    checkFormList: (
        questionList: Array<IAppFormQuestion>,
        answerList: Array<IAnswerProps>,
        selectors?: IAppFormSelectors
    ) => boolean;
    checkForm: (answerList: Array<IAnswerProps>) => boolean;
    checkFormCheckList: (answerList: Array<IAnswerProps>) => boolean;
    createAnswerList: (
        questionList: Array<IAppFormQuestion>,
        formAnswerList: Array<IAnswerProps>,
        answerParam: IAnswerProps
    ) => Array<IAnswerProps>;
    setupPageNavigation: (params: string, applicantList: IApplicantInfo[], applicantParams: string, forwardNav: boolean) => string;
    setupPageDefaultValue: (questionId: number, info: IApplicantInfo) => string;
    setupPageUpdateValue: (
        params: string,
        answerParams: Array<IAnswerProps>
    ) => IApplicantInfo;
    filterFirstnameLabel: (question: IAppFormQuestion, info: IApplicantInfo) => string;
    filterButtonLabel: (question: IAppFormQuestion, answer: string) => string;
    filterAddressLabel: (
        label: string,
        info: IApplicantInfo,
        isMoreApplicant: boolean
    ) => string;
    buttonVisibility: (controlType: number, answer: string) => boolean;
    validDOB: (question: IAppFormQuestion, date: string) => string;
    validEmail: (email: string) => boolean;
    checkValidApplication: (applicationId: string, selectors?: IAppFormSelectors) => void;
    getGenderString: (gender: number) => string;
    checkSearchTerm: (search: string) => boolean;
    populateDataLayer: (applicationId: number, quoteGroupId: string) => void;
    getClientNumberFromParam: (param: string, applicants: IApplicantInfo[]) => number;
}

function checkFormList(questionList, answerList, selectors): boolean {
    return questionList.reduce((acc, item) => {
        const formAnswer =
            selectors && item?.id === 1
                ? selectors?.getEachFormData(item.id)?.answer ?? ""
                : "";
        const answerValue = answerList.find((i) => i.questionId === item.id);
        const answer = answerValue?.answer ?? formAnswer;
        const isFieldRequired =
            item?.required === undefined || item?.required === null || item?.required;
        if (isFieldRequired === true && checkInValidAnswer(answer)) {
            acc = false;
        }
        return acc;
    }, true);
}

function checkForm(answerList): boolean {
    return answerList.reduce((acc, item) => {
        if (checkInValidAnswer(item?.answer)) {
            acc = false;
        }
        return acc;
    }, true);
}

function checkFormCheckList(answerList): boolean {
    return answerList.reduce((acc, item) => {
        if (item?.answer === "Yes") {
            acc = true;
        }
        return acc;
    }, false);
}

function createAnswerList(
    questionList,
    formAnswerList,
    answerParam
): Array<IAnswerProps> {
    return questionList.reduce((acc, item) => {
        const currentAnswer = formAnswerList.find((i) => i.questionId === item.id);
        const answer =
            answerParam.questionId === item.id
                ? answerParam.answer
                : currentAnswer?.answer;

        return [
            ...acc,
            {
                questionId: item.id,
                disclosure: [],
                answer
            }
        ];
    }, []);
}

function setupPageNavigation(params: string, applicantList: IApplicantInfo[], applicantParams: string, forwardNav = true): string {
    if (forwardNav) {
        switch (params) {
            case "info":
                return `/app-form/setup/personal/${applicantParams}`;
            case "gender":
                return `/app-form/setup/smoked/${applicantParams}`;
            case "smoked":
                return `/app-form/setup/personal/${applicantParams}`;
            case "personal":
                return `/app-form/setup/dateofbirth/${applicantParams}`;
            default: //dateofbirth also uses this default route
                return `/app-form/setup/contact/${applicantParams}`;
        }
    } else {
        switch (params) {
            case "contact":
                return `/app-form/setup/dateofbirth/${applicantParams}`;
            case "dateofbirth":
                return `/app-form/setup/personal/${applicantParams}`;
            case "smoked":
                return `/app-form/setup/gender/${applicantParams}`;
            case "info":
                const currentApplicant = applicantList.find((x) => x.params == applicantParams);
                const previousApplicant = applicantList.find((x) => x.clientNumber === currentApplicant.clientNumber - 1);
                if (previousApplicant) {
                    if (previousApplicant.isChild) {
                        return `/app-form/setup/dateofbirth/applicant${previousApplicant.clientNumber}`;
                    }
                    return `/app-form/setup/contact/applicant${previousApplicant.clientNumber}`;
                };
            default: //both gender and personal also use this default route
                return `/app-form/setup/info/${applicantParams}`;
        }
    }
}

function setupPageDefaultValue(questionId, info): string {
    switch (questionId) {
        case SetupQuestion.INFO:
            return info?.isInfoUnchange;
        case SetupQuestion.TITLE:
            return info?.title;
        case SetupQuestion.FIRST_NAME:
            return info?.firstName;
        case SetupQuestion.MIDDLE_NAME:
            return info?.middleName;
        case SetupQuestion.LAST_NAME:
            return info?.lastName;
        case SetupQuestion.GENDER:
            return !info?.isChangeGender ? info?.gender : "";
        case SetupQuestion.SMOKER_STATUS:
            return !info?.isChangeSmoked ? `${info?.isSmoker}` : "";
        case SetupQuestion.DATEOFBIRTH:
            return info?.dateOfBirth || "";
        case SetupQuestion.EMAIL:
            return info?.email || "";
        case SetupQuestion.PHONE:
            return info?.phone || "";
        case SetupQuestion.CONTACT_TIME:
            return info?.bestContactTime || "";
        default:
            return "";
    }
}

function setupPageUpdateValue(params, answerParams): IApplicantInfo {
    const answerInfo = answerParams?.length && answerParams[0];
    switch (params) {
        case "info":
            return {
                isInfoUnchange: answerInfo?.answer
            };
        case "dateofbirth":
            return {
                dateOfBirth: answerInfo?.answer
            };
        case "gender":
            return {
                gender: answerInfo?.answer,
                isChangeGender: false
            };
        case "smoked":
            return {
                isSmoker: answerInfo?.answer,
                isChangeSmoked: false
            };
        case "personal":
            return {
                title:
                    answerParams.find((i) => i.questionId === SetupQuestion.TITLE)
                        ?.answer || "",
                firstName:
                    answerParams.find((i) => i.questionId === SetupQuestion.FIRST_NAME)
                        ?.answer || "",
                middleName:
                    answerParams.find((i) => i.questionId === SetupQuestion.MIDDLE_NAME)
                        ?.answer || "",
                lastName:
                    answerParams.find((i) => i.questionId === SetupQuestion.LAST_NAME)
                        ?.answer || ""
            };
        case "contact":
            return {
                email:
                    answerParams.find((i) => i.questionId === SetupQuestion.EMAIL)
                        ?.answer || "",
                phone:
                    answerParams.find((i) => i.questionId === SetupQuestion.PHONE)
                        ?.answer || "",
                bestContactTime:
                    answerParams.find((i) => i.questionId === SetupQuestion.CONTACT_TIME)
                        ?.answer || ""
            };
        default:
            return {};
    }
}

function filterFirstnameLabel(question, info: IApplicantInfo): string {
    const applicantFirstname = info?.firstName;
    switch (question?.id) {
        case 1:
            const smoker = info?.isSmoker ? "smoker" : "non-smoker";
            if (info?.isChild) {
                return `Is ${info?.label} a ${getGenderString(info?.gender)} child?`;
            } else {
                return `Is ${info?.label} a ${getGenderString(info?.gender)}, ${info?.age} year old ${smoker}?`;
            }
        case 2:
        case 3:
        case 6:
        case 7:
            return info?.label
                ? question?.questionLabel.replace("applicant", info?.label)
                : question?.questionLabel;
        case 8:
        case 9:
            return applicantFirstname
                ? question?.questionLabel.replace("firstname", applicantFirstname)
                : question?.questionLabel;
        case 5:
            return applicantFirstname
                ? question?.questionLabel.replace("firstname", applicantFirstname)
                : question?.questionLabel;
        case 11:
        case 12:
        case 13:
            return applicantFirstname
                ? question?.questionLabel.replace("firstname", applicantFirstname)
                : question?.questionLabel;
        default:
            return question?.questionLabel;
    }
}

function filterButtonLabel(question, answer): string {
    if (!question?.id) return "Next";

    switch (question.id) {
        case 8:
            return !answer ? "Skip" : "Next";
        default:
            return "Next";
    }
}

function filterAddressLabel(label, info, isMoreApplicant): string {
    if (!isMoreApplicant) {
        return label.replace("firstNamePlaceholder", `your`);
    }
    return info?.firstName
        ? label.replace("firstNamePlaceholder", `${info?.firstName}'s`)
        : label;
}

function buttonVisibility(controlType, answer): boolean {
    switch (controlType) {
        case ControlType.RADIOBUTTON:
            const buttonStatus = !!String(answer).trim() || false;
            return buttonStatus;
        default:
            return true;
    }
}

function validDOB(question, date): string {
    const errorMessage = `Please provide a date from ${question?.rangeStart} to ${question?.rangeEnd} years old`;
    if ((!question?.rangeStart && question?.rangeStart !== 0) || !question?.rangeEnd) return "";
    const datePicked = new Date(date);
    if (datePicked.toDateString() === new Date().toDateString()) {
        return errorMessage;
    }
    var diff_ms = Date.now() - datePicked.getTime();
    if (diff_ms < 0) {
        return errorMessage;
    }
    var age_dt = new Date(diff_ms);

    const age = Math.abs(age_dt.getUTCFullYear() - 1970);
    if (age >= question?.rangeStart && age <= question?.rangeEnd) return "";
    return errorMessage;
}

function validEmail(email): boolean {
    return /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(email);
}

const checkValidApplication = (applicationId, selectors) => {
    if (String(selectors.getApplicationId()) !== applicationId) {
        redirectTo404Page();
    }
};

const getGenderString = (gender: number) => {
    switch (Number(gender)) {
        case 1:
            return "male";
        case 2:
            return "female";
        default:
            return "unknown";
    }
}

const checkSearchTerm = (search: string) => {
    if ((search.length > 0 && search.length < 3) || search.length > 200) {
        return false
    }
    return true;
}

const populateDataLayer = (applicationId: number, quoteGroupId: string) => {
    window.dataLayer = window.dataLayer || [];
    getDataLayerInfo(applicationId, quoteGroupId)
        .then((res) => {
            window.FamilyID = res.familyId;

            window.dataLayer.push({
                "FamilyID": res.familyId,
                "AppID": res.appId,
                "VisitorID": res.visitorId,
                "CollectionID": res.collectionId,
                "InsurerName": res.insurerName,
                "API": res.api,
                "CurrentPageProgress": res.currentPageProgress,
                "Benefits": res.benefits
            });
        });  
}

const getClientNumberFromParam = (param: string, applicants: IApplicantInfo[]) => {
    if (param.includes("applicant")) {
        return Number(param.split("applicant")[1]);
    } else {
        const applicant = applicants.find((a) => a.applicantToken === param);
        return applicant === undefined ? 0 : applicant.clientNumber;
    }
}

const useAFForm = (): IUseAFForm => {
    return {
        checkFormList,
        checkForm,
        checkFormCheckList,
        createAnswerList,
        setupPageNavigation,
        setupPageDefaultValue,
        setupPageUpdateValue,
        filterFirstnameLabel,
        filterButtonLabel,
        filterAddressLabel,
        buttonVisibility,
        validDOB,
        validEmail,
        checkValidApplication,
        getGenderString,
        checkSearchTerm,
        populateDataLayer,
        getClientNumberFromParam
    };
};

export default useAFForm;
